import React from 'react';
import styles from './ScoreCard.module.css';

interface AffectData {
  value: number;
  risk: string;
  trend: 'up' | 'down' | 'stable';
  lastUpdated: string;
}

interface ScoreCardProps {
  title: string;
  score?: number;
  maxScore?: number;
  scoreLabel?: string;
  riskLevel?: 'Low' | 'Moderate' | 'High' | 'Minimal';
  riskColor?: 'green' | 'yellow' | 'red';
  lastUpdated: string;
  description?: string;
  customStyle?: React.CSSProperties;
  affectType?: 'dual' | 'single';
  affects?: {
    label: string;
    positive: AffectData;
    negative: AffectData;
  }[];
  circleLabels?: [string, string];
  onViewDetails?: () => void;
}

const ScoreCard: React.FC<ScoreCardProps> = ({
  title,
  score = 0,
  maxScore = 10,
  scoreLabel = '',
  lastUpdated,
  description,
  customStyle,
  affectType = 'single',
  affects = [],
  circleLabels = ['POS', 'NEG'],
  onViewDetails,
}) => {
  const renderAffectCircles = () => (
    <div className={styles.affectContainer}>
      {affects.map((affect, index) => {
        const isHigh = affect.positive.risk.toLowerCase() === 'high';

        return (
          <div key={index} className={styles.affectSection}>
            <div className={styles.affectHeader}>{affect.label.toUpperCase()}</div>
            <div className={styles.affectCircles}>
              <div
                className={`${styles.affectCircle} ${isHigh ? styles.active : ''}`}
                onClick={() => onViewDetails?.()}
              >
                <div className={styles.affectValue}>
                  <span className={styles.affectLabel}>{circleLabels[0]}</span>
                </div>
              </div>
              <div
                className={`${styles.affectCircle} ${!isHigh ? styles.active : ''}`}
                onClick={() => onViewDetails?.()}
              >
                <div className={styles.affectValue}>
                  <span className={styles.affectLabel}>{circleLabels[1]}</span>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderScoreCard = () => (
    <>
      <div className={styles.scoreCircle}>
        <div className={styles.scoreValue}>{score}</div>
        <div className={styles.scoreMax}>/{maxScore}</div>
      </div>

      <div className={styles.scoreInfo}>
        <div className={styles.scoreLabel}>{scoreLabel}</div>
        {description && (
          <div className={styles.scoreDescription}>{description}</div>
        )}
        <div className={styles.lastUpdated}>Last Updated: {lastUpdated}</div>
      </div>
    </>
  );

  return (
    <div className={styles.scoreCardContainer} style={customStyle}>
      <div className={styles.scoreCardHeader}>
        <h3 className={styles.scoreCardTitle}>{title}</h3>
      </div>

      <div className={styles.scoreCardContent}>
        {affectType === 'dual' && affects.length > 0
          ? renderAffectCircles()
          : renderScoreCard()}
      </div>
    </div>
  );
};

export default ScoreCard;
